import { Canvas } from '@react-three/fiber'
import { Table } from 'antd'
import classNames from 'classnames'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import { resolveModelVersionFromApiAndPatientSex } from 'features/Procedure/hooks/useEvaluateModel'
import { WEBGL_DPR_DUAL_BODY } from 'features/Procedure/procedure-model/canvas-performance'
import type { ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import { useFetchProceduresQuery } from 'features/Procedure/state/procedure.api'
import { ProcedureInitialState } from 'features/Procedure/state/procedure.slice'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { processDrawingsData } from 'features/Procedure/utils/api.utils'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { baseColumns } from './columns'
import { DiagnosticCell } from './components/diagnostic-cell'
import { DualBodyView } from './components/dual-body-view'
import styles from './treatments-dual.module.scss'

const MODEL_BASE_PATH = '/models'

export type TreatmentsDualComparisonProps = {
  selectedPatientId: number | null
  selectedPatientSex?: string | null
  selectedEncounterUid: string | null
  selectedComparisonEncounterUid: string | null
  className?: string
  onCanvasReady?: () => void
}

export const TreatmentsDualComparison = ({
  selectedPatientSex,
  selectedEncounterUid,
  selectedComparisonEncounterUid,
  className,
  onCanvasReady,
}: TreatmentsDualComparisonProps) => {
  const { siteId } = useParams()
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const { data: mainProceduresData, isFetching: isMainFetching } = useFetchProceduresQuery(
    {
      siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  )

  const { data: comparisonProceduresData, isFetching: isComparisonFetching } =
    useFetchProceduresQuery(
      {
        siteId,
        encounterUid: selectedComparisonEncounterUid,
      },
      {
        skip: !selectedComparisonEncounterUid,
        refetchOnReconnect: true,
        refetchOnMountOrArgChange: true,
      },
    )

  const isFetching = isMainFetching || isComparisonFetching

  const mainItems = useMemo(() => {
    if (!mainProceduresData?.items || !selectedEncounterUid) return []
    return mainProceduresData.items?.filter((item) => item.code_type === ProcedureTypeCode.CPT4)
  }, [mainProceduresData, selectedEncounterUid])

  const comparisonItems = useMemo(() => {
    if (!comparisonProceduresData?.items || !selectedComparisonEncounterUid) return []
    return comparisonProceduresData.items?.filter(
      (item) => item.code_type === ProcedureTypeCode.CPT4,
    )
  }, [comparisonProceduresData, selectedComparisonEncounterUid])

  const mainColumns = useMemo(
    () => [
      {
        title: 'Diagnosis',
        dataIndex: 'justify_codes',
        key: 'justify_codes',
        render: (_: string, item: ProcedureItem, index: number) => (
          <DiagnosticCell
            justifyCodes={item.justify_codes}
            selectedEncounterUid={selectedEncounterUid}
            count={index + 1}
          />
        ),
      },
      ...baseColumns(),
    ],
    [selectedEncounterUid],
  )

  const comparisonColumns = useMemo(
    () => [
      {
        title: 'Diagnosis',
        dataIndex: 'justify_codes',
        key: 'justify_codes',
        render: (_: string, item: ProcedureItem, index: number) => (
          <DiagnosticCell
            justifyCodes={item.justify_codes}
            selectedEncounterUid={selectedComparisonEncounterUid}
            count={index + 1}
          />
        ),
      },
      ...baseColumns(),
    ],
    [selectedComparisonEncounterUid],
  )

  const mainDrawingElements = useMemo(() => {
    if (!mainProceduresData) return []
    return processDrawingsData(mainProceduresData)
  }, [mainProceduresData])

  const comparisonDrawingElements = useMemo(() => {
    if (!comparisonProceduresData) return []
    return processDrawingsData(comparisonProceduresData)
  }, [comparisonProceduresData])

  const mainModelPath = useMemo(() => {
    if (!mainProceduresData) return null
    const modelVersion = resolveModelVersionFromApiAndPatientSex(
      mainProceduresData.model_version,
      selectedPatientSex,
    )
    return `${MODEL_BASE_PATH}/${modelVersion}.glb`
  }, [mainProceduresData, selectedPatientSex])

  const comparisonModelPath = useMemo(() => {
    if (!comparisonProceduresData) return null
    const modelVersion = resolveModelVersionFromApiAndPatientSex(
      comparisonProceduresData.model_version,
      selectedPatientSex,
    )
    return `${MODEL_BASE_PATH}/${modelVersion}.glb`
  }, [comparisonProceduresData, selectedPatientSex])

  const cameraPosition: [number, number, number] = [
    ProcedureInitialState.scene.position.x,
    ProcedureInitialState.scene.position.y,
    ProcedureInitialState.scene.position.z,
  ]

  const canRenderBodies = !isFetching && mainModelPath && comparisonModelPath

  const [isCanvasReady, setIsCanvasReady] = useState(false)

  useEffect(() => {
    if (!canRenderBodies) setIsCanvasReady(false)
  }, [canRenderBodies])

  const handleFirstRender = useCallback(() => {
    setIsCanvasReady(true)
    onCanvasReady?.()
  }, [onCanvasReady])

  return (
    <div className={classNames(styles.dualContainer, className)}>
      <div className={styles.tablesRow}>
        <div className={styles.tableColumn}>
          <div className={styles.tableHeader}>Main Encounter</div>
          <div className={styles.tableWrapper}>
            <Table
              rowKey='id'
              dataSource={mainItems}
              columns={mainColumns}
              loading={isMainFetching}
              tableLayout='auto'
              bordered
              className={styles.table}
              pagination={false}
              size='small'
            />
          </div>
        </div>
        <div className={styles.tableColumn}>
          <div className={styles.tableHeader}>Comparison Encounter</div>
          <div className={styles.tableWrapper}>
            <Table
              rowKey='id'
              dataSource={comparisonItems}
              columns={comparisonColumns}
              loading={isComparisonFetching}
              tableLayout='auto'
              bordered
              className={styles.table}
              pagination={false}
              size='small'
            />
          </div>
        </div>
      </div>
      <div className={styles.bodyContainer}>
        <SpinnerOverlay isLoading={!isCanvasReady} />
        <div className={styles.viewportLabels}>
          <span className={styles.viewportLabel}>Main Encounter</span>
          <span className={styles.viewportLabel}>Comparison Encounter</span>
        </div>
        <div className={styles.divider} />
        {canRenderBodies && (
          <Canvas
            ref={canvasRef}
            className={styles.canvas}
            camera={{ position: cameraPosition, fov: 40 }}
            dpr={WEBGL_DPR_DUAL_BODY}
            gl={{ antialias: true, powerPreference: 'default' }}
            frameloop='demand'>
            <Suspense fallback={null}>
              <DualBodyView
                leftBody={{
                  drawingElements: mainDrawingElements,
                  modelPath: mainModelPath,
                  skinColor: '#FFDBAC',
                }}
                rightBody={{
                  drawingElements: comparisonDrawingElements,
                  modelPath: comparisonModelPath,
                  skinColor: '#FFDBAC',
                }}
                onFirstRender={handleFirstRender}
              />
            </Suspense>
          </Canvas>
        )}
      </div>
    </div>
  )
}
